import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
} from '@mui/material';
import ItemList from 'components/item/ItemList';
import {
  GRAPHQL_AUTH_MODE,
  getGQLAPI,
  navStore,
  useStore,
} from '@mcn-platform/models';
import { getMemberByCustomerID } from 'graphql/queries';
import ReferenceSelector from 'components/forms/ReferenceSelector';
import { v4 } from 'uuid';
import { useNavigate } from 'react-router';
import SelectionModel from 'components/item/SelectionModel';
import { observer } from 'mobx-react-lite';

const calculateExpirationDate = (membership) => {
  const currentDate = new Date();
  let expiresOn = null as Date | null;

  if (membership.paymentPlan === 'onetime') {
    if (membership.onetimeEnableExpiry && membership.onetimeExpireDays) {
      expiresOn = new Date(currentDate.setDate(
        currentDate.getDate() + membership.onetimeExpireDays,
      ));
    }
  } else if (membership.paymentPlan === 'installments') {
    if (membership.installmentEnableExpiry && membership.installmentExpireDays) {
      expiresOn = new Date(currentDate.setDate(
        currentDate.getDate() + membership.installmentExpireDays,
      ));
    }
  }
  return expiresOn ? expiresOn.toISOString() : null;
};

/** Uses Dynamo GSI `getMemberByCustomerID` — avoids full-table `listMembers`. */
async function loadMembersViaCustomerGsi(
  store: ReturnType<typeof useStore>,
  organizationID: string,
  customerID: string,
): Promise<unknown[]> {
  const compositeKey = `${organizationID}~${customerID}`;
  const variants = [...new Set([customerID, compositeKey].filter(Boolean))];
  const byId = new Map<string, Record<string, unknown>>();
  for (const custKey of variants) {
    let nextToken: string | null | undefined = null;
    do {
      /* eslint-disable no-await-in-loop -- sequential pagination per variant */
      const res = await getGQLAPI().graphql({
        query: getMemberByCustomerID,
        variables: {
          organizationID,
          customerID: { eq: custKey },
          limit: 1000,
          nextToken: nextToken ?? null,
        },
        authMode: GRAPHQL_AUTH_MODE.AWS_IAM,
      }) as { data?: { getMemberByCustomerID?: { items?: Record<string, unknown>[]; nextToken?: string | null } }; errors?: { message?: string }[] };
      if (res.errors?.length) {
        throw new Error(res.errors.map((e) => e.message).join('; '));
      }
      const conn = res.data?.getMemberByCustomerID;
      const items = conn?.items ?? [];
      for (const row of items) {
        if (row?.id != null) byId.set(String(row.id), row);
      }
      nextToken = conn?.nextToken;
    } while (nextToken);
  }
  return [...byId.values()].map((row) => store.remoteItemToLocal('member', row));
}

const NewMemberDialog = ({
  open,
  handleNewMembership,
  handleCancelDialog,
  customerID,
  customerName,
  customerEmail,
}:any) => {
  const [membership, setMembership] = useState(null);
  return (
    <Dialog open={open}>
      <DialogContent>
        <DialogTitle>
          Add Membership for
          {' '}
          {customerName || customerEmail || customerID}
        </DialogTitle>
        {/* {portal?.name} */}
        {/* @TODO - filter by portal */}
        <ReferenceSelector
          label="Membership"
          modelName="membership"
          modelScope="portal"
          id={`membership-${customerID}`}
          onChange={(newMembership:any) => {
            console.log('membership selected', membership);
            setMembership(newMembership);
          }}
          value={membership}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelDialog}>Cancel</Button>
        <Button
          disabled={!membership}
          onClick={() => { handleNewMembership(membership); }}
        >
          Add Membership
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * This is a special case field because it doesn't actually effect any data
 * on the form (ie the the Customer).  Instead it provides a reference
 * to the many to many connection to the Membership table via the Member table.
 * @param param0
 * @returns
 */

const MembersListField = ({
  organizationID,
  customerID,
  customerName,
  customerEmail,
}:any) => {
  const [memberItems, setMemberItems] = useState<unknown[]>([]);
  const [listLoading, setListLoading] = useState<boolean>(true);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const store = useStore();

  const loadList = useCallback(async () => {
    if (!organizationID || !customerID) {
      setMemberItems([]);
      setListLoading(false);
      return;
    }
    setListLoading(true);
    try {
      const items = await loadMembersViaCustomerGsi(store, organizationID, customerID);
      setMemberItems(items);
    } catch (e) {
      console.error('MembersListField: load members failed', e);
      setMemberItems([]);
    } finally {
      setListLoading(false);
    }
  }, [organizationID, customerID, store]);

  useEffect(() => {
    loadList();
  }, [organizationID, customerID, loadList]);

  const handleNewMembership = async (membershipID) => {
    setDialogOpen(false);
    navStore.showMessageDialog(
      'Creating Membership',
      'Membership is being created for the customer. Please wait.',
    );
    try {
      if (!membershipID) {
        console.log('No membershipID, skipping membersip create');
      }
      const membership = await store.getOrLoad('membership', membershipID);
      if (membership) {
        console.log('Create new membership', membership);
        const newMember = await store.stub(
          'member',
          v4(),
          customerEmail,
          {
            customerID,
            membershipID: membership.id,
            portalID: membership.portal.id,
            status: 'ACTIVE',
            expiresOn: calculateExpirationDate(membership),
          },
        );
        console.log('New member', newMember);
        await store.addStub('member', newMember);
        await store.refreshCustomerStats(customerID);
        loadList();
        navStore.hideMessageDialog();
      }
    } catch (e) {
      navStore.showMessageDialog('Error', 'Error creating the membership.');
    }
  };

  const viewMember = (member:any) => {
    navigate(`/app/member-editor/${member.id}`);
  };

  const model = new SelectionModel(
    false,
    memberItems,
    [],
    [],
    [],
  );

  // const items = get(formik.values, propName);
  return (
    <FormControlLabel
      label="Memberships"
      labelPlacement="top"
      onClick={(e) => {
        e.preventDefault();
      }}
      control={(
        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }} />
          <ItemList
            modelName="membership"
            model={model}
            isStillLoading={listLoading}
            // initialselectedIDs={initialselectedIDs}
            listStyle="list"
            // defaultAction={defaultAction}
            // actions={actions}
            // imageField={imageField}
            // titleField={titleField}
            defaultAction={viewMember}
            actions={[
              {
                name: 'view',
                label: 'View',
                action: viewMember,
              },
            ]}
            imageField={{ name: 'image', label: 'Poster' }}
            titleField={{ name: 'membership.name', label: 'Membership' }}
            secondaryFields={[
              // { name: 'rating', label: 'Rating' },
              { name: 'portal.name', label: 'Portal' },
              { name: 'name', label: 'Email' },
              { name: 'status', label: 'Status' },
            ]}
            showSelect={false}
            enableDeselect={false}
          />

          <Button onClick={() => setDialogOpen(true)}>Add Membership</Button>
          <NewMemberDialog
            open={dialogOpen}
            customerID={customerID}
            customerName={customerName}
            customerEmail={customerEmail}
            handleCancelDialog={() => setDialogOpen(false)}
            handleNewMembership={(membership) => handleNewMembership(membership)}
          />
        </Box>
    )}
    />
  );
};

export default observer(MembersListField);
